import React from 'react';

// SVG board drawn from the viewer's side: your home board bottom-right,
// your checkers travel 24 -> 1 counter-clockwise, your bear-off tray on the
// right. Everything is in your point numbering; opponent point q is 25 - q.

const H = 760;
const FRAME = 24;
const PW = 62;            // point width
const BAR_W = 64;
const TRAY_W = 86;
const RAIL = 66;          // left rail holds the cube
const LEFT_X = RAIL;
const RIGHT_X = LEFT_X + 6 * PW + BAR_W;
const BOARD_RIGHT = RIGHT_X + 6 * PW;
const TRAY_X = BOARD_RIGHT + 14;
const TOP_Y = FRAME;
const BOT_Y = H - FRAME;
const POINT_H = 290;
const R = 27;             // checker radius
const MAX_STACK = 5;

export function pointX(p) {
  // Left edge of my point p.
  if (p >= 1 && p <= 6) return RIGHT_X + (6 - p) * PW;
  if (p >= 7 && p <= 12) return LEFT_X + (12 - p) * PW;
  if (p >= 13 && p <= 18) return LEFT_X + (p - 13) * PW;
  return RIGHT_X + (p - 19) * PW;
}

const isBottom = (p) => p <= 12;

function checkerPos(p, i) {
  const cx = pointX(p) + PW / 2;
  const slot = i % MAX_STACK;
  const layer = Math.floor(i / MAX_STACK);
  const off = R + slot * (2 * R) + layer * R * 0.5;
  return [cx, isBottom(p) ? BOT_Y - off : TOP_Y + off];
}

function Point({ p, highlight, source, lastMove, onClick }) {
  const x = pointX(p);
  const bottom = isBottom(p);
  const tipY = bottom ? BOT_Y - POINT_H : TOP_Y + POINT_H;
  const baseY = bottom ? BOT_Y : TOP_Y;
  const dark = p % 2 === 0;
  return (
    <g onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <rect x={x} y={bottom ? BOT_Y - POINT_H - 10 : TOP_Y} width={PW} height={POINT_H + 10} fill="transparent" />
      <polygon
        points={`${x + 2},${baseY} ${x + PW - 2},${baseY} ${x + PW / 2},${tipY}`}
        className={`bg-point ${dark ? 'bg-point-dark' : 'bg-point-light'} ${lastMove ? 'bg-point-last' : ''}`}
      />
      {highlight && (
        <polygon
          points={`${x + 2},${baseY} ${x + PW - 2},${baseY} ${x + PW / 2},${tipY}`}
          className="bg-point-target"
        />
      )}
      {source && <rect x={x + 4} y={bottom ? BOT_Y - 6 : TOP_Y} width={PW - 8} height={6} className="bg-point-source" />}
      <text x={x + PW / 2} y={bottom ? H - FRAME / 2 : FRAME / 2} dominantBaseline="central" className="bg-point-num">{p}</text>
    </g>
  );
}

function Stack({ p, count, mine, selected, onClick }) {
  const els = [];
  for (let i = 0; i < count; i++) {
    const [cx, cy] = checkerPos(p, i);
    els.push(
      <circle key={i} cx={cx} cy={cy} r={R - 1}
        className={`bg-checker ${mine ? 'bg-checker-me' : 'bg-checker-opp'} ${selected && i === count - 1 ? 'bg-checker-selected' : ''}`} />
    );
  }
  if (count > MAX_STACK) {
    const [cx, cy] = checkerPos(p, count - 1);
    els.push(<text key="n" x={cx} y={cy + 6} className="bg-stack-count">{count}</text>);
  }
  return <g onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>{els}</g>;
}

function Die({ x, y, value, used, size = 48, owner }) {
  const pips = {
    1: [[0.5, 0.5]], 2: [[0.25, 0.25], [0.75, 0.75]], 3: [[0.25, 0.25], [0.5, 0.5], [0.75, 0.75]],
    4: [[0.25, 0.25], [0.75, 0.25], [0.25, 0.75], [0.75, 0.75]],
    5: [[0.25, 0.25], [0.75, 0.25], [0.5, 0.5], [0.25, 0.75], [0.75, 0.75]],
    6: [[0.25, 0.22], [0.75, 0.22], [0.25, 0.5], [0.75, 0.5], [0.25, 0.78], [0.75, 0.78]],
  }[value] || [];
  return (
    <g opacity={used ? 0.3 : 1}>
      <rect x={x} y={y} width={size} height={size} rx={8} className={`bg-die ${owner === 'me' ? 'bg-die-me' : 'bg-die-opp'}`} />
      {pips.map(([px, py], i) => (
        <circle key={i} cx={x + px * size} cy={y + py * size} r={size * 0.09} className={`bg-pip ${owner === 'me' ? 'bg-pip-me' : 'bg-pip-opp'}`} />
      ))}
    </g>
  );
}

export default function Board({
  me, opp, selected, targets, sources, lastMovePoints,
  dice, diceOwner, cube, onPoint, onBar, onTray, onDice, labels, crawford,
  onRoll, onConfirm, onDouble, doubleValue, offer,
}) {
  const targetSet = new Set(targets || []);
  const sourceSet = new Set(sources || []);
  const lastSet = new Set(lastMovePoints || []);
  const midY = H / 2;

  const points = [];
  for (let p = 1; p <= 24; p++) {
    const clickable = onPoint && (targetSet.has(p) || sourceSet.has(p) || selected === p);
    points.push(
      <Point key={p} p={p} highlight={targetSet.has(p)} source={sourceSet.has(p) && selected !== p}
        lastMove={lastSet.has(p)} onClick={clickable ? () => onPoint(p) : undefined} />
    );
  }

  const stacks = [];
  for (let p = 1; p <= 24; p++) {
    const mine = me[p];
    const theirs = opp[25 - p];
    if (mine) {
      const clickable = onPoint && (sourceSet.has(p) || targetSet.has(p) || selected === p);
      stacks.push(<Stack key={`m${p}`} p={p} count={mine} mine selected={selected === p}
        onClick={clickable ? () => onPoint(p) : undefined} />);
    }
    if (theirs) {
      const clickable = onPoint && targetSet.has(p);
      stacks.push(<Stack key={`o${p}`} p={p} count={theirs} mine={false}
        onClick={clickable ? () => onPoint(p) : undefined} />);
    }
  }

  const barX = LEFT_X + 6 * PW;
  const barCx = barX + BAR_W / 2;
  // pointerEvents:none so a click on a bar checker body falls through to the bar
  // rect beneath it (which carries the onBar handler) instead of being swallowed.
  const barCheckers = [];
  for (let i = 0; i < me[25]; i++) {
    barCheckers.push(<circle key={`bm${i}`} cx={barCx} cy={midY + 50 + i * 20} r={R - 1} style={{ pointerEvents: 'none' }}
      className={`bg-checker bg-checker-me ${selected === 25 && i === me[25] - 1 ? 'bg-checker-selected' : ''}`} />);
  }
  for (let i = 0; i < opp[25]; i++) {
    barCheckers.push(<circle key={`bo${i}`} cx={barCx} cy={midY - 50 - i * 20} r={R - 1} style={{ pointerEvents: 'none' }} className="bg-checker bg-checker-opp" />);
  }

  // Borne-off trays: thin slabs.
  const tray = (count, mineSide) => {
    const els = [];
    for (let i = 0; i < count; i++) {
      const y = mineSide ? BOT_Y - 12 - i * 14 : TOP_Y + 2 + i * 14;
      els.push(<rect key={i} x={TRAY_X + 10} y={y} width={TRAY_W - 20} height={10} rx={3}
        className={`bg-off ${mineSide ? 'bg-checker-me' : 'bg-checker-opp'}`} />);
    }
    return els;
  };
  const trayTarget = targetSet.has(0);

  // Cube: centered on the left rail, or on the owner's side.
  let cubeY = midY - 26;
  if (cube && cube.owner === 'me') cubeY = BOT_Y - 70;
  if (cube && cube.owner === 'opp') cubeY = TOP_Y + 18;

  // dice: [{value, used}] — 2 entries, or 4 for doubles.
  const dsize = dice && dice.length > 2 ? 42 : 50;
  const dgap = dsize + 10;
  const dwidth = dice ? dice.length * dgap - 10 : 0;
  const diceX = (diceOwner === 'me' ? RIGHT_X : LEFT_X) + 3 * PW - dwidth / 2;

  return (
    <svg viewBox={`0 0 ${TRAY_X + TRAY_W + 6} ${H}`} className="bg-board" preserveAspectRatio="xMidYMid meet">
      <rect x={0} y={0} width={TRAY_X + TRAY_W + 6} height={H} rx={14} className="bg-frame" />
      <rect x={RAIL - 8} y={TOP_Y} width={8} height={H - 2 * FRAME} className="bg-frame" />
      <rect x={LEFT_X} y={TOP_Y} width={6 * PW} height={H - 2 * FRAME} className="bg-felt" />
      <rect x={RIGHT_X} y={TOP_Y} width={6 * PW} height={H - 2 * FRAME} className="bg-felt" />
      <rect x={barX} y={TOP_Y} width={BAR_W} height={H - 2 * FRAME} className="bg-bar"
        onClick={onBar && me[25] ? onBar : undefined} style={{ cursor: onBar && me[25] ? 'pointer' : 'default' }} />
      <g onClick={trayTarget && onTray ? onTray : undefined} style={{ cursor: trayTarget ? 'pointer' : 'default' }}>
        <rect x={TRAY_X} y={TOP_Y} width={TRAY_W} height={H - 2 * FRAME} rx={6}
          className={`bg-tray ${trayTarget ? 'bg-tray-target' : ''}`} />
        {tray(opp[0], false)}
        {tray(me[0], true)}
      </g>
      {points}
      {stacks}
      {barCheckers}
      {labels && labels.top && <text x={TRAY_X + TRAY_W / 2} y={midY - 14} className="bg-tray-label">{labels.top}</text>}
      {labels && labels.bottom && <text x={TRAY_X + TRAY_W / 2} y={midY + 26} className="bg-tray-label">{labels.bottom}</text>}
      {cube && !crawford && (
        <g>
          <rect x={9} y={cubeY} width={48} height={52} rx={7} className="bg-cube" />
          <text x={33} y={cubeY + 35} className="bg-cube-text">{cube.value === 1 ? 64 : cube.value}</text>
        </g>
      )}
      {crawford && <text x={33} y={midY + 6} className="bg-crawford" transform={`rotate(-90 33 ${midY})`}>CRAWFORD</text>}
      {dice && (
        <g onClick={onDice} style={{ cursor: onDice ? 'pointer' : 'default' }}>
          {dice.map((d, i) => (
            <Die key={i} x={diceX + i * dgap} y={midY - dsize / 2} size={dsize} value={d.value} used={d.used} owner={diceOwner} />
          ))}
        </g>
      )}
      {onRoll && (
        <g className="bg-onboard" onClick={onRoll} style={{ cursor: 'pointer' }}>
          <rect x={RIGHT_X + 3 * PW - 78} y={midY - 32} width={156} height={64} rx={13} className="bg-onboard-roll" />
          <text x={RIGHT_X + 3 * PW} y={midY} dominantBaseline="central" className="bg-onboard-label">ROLL</text>
        </g>
      )}
      {onConfirm && (
        <g className="bg-onboard" onClick={onConfirm} style={{ cursor: 'pointer' }}>
          <rect x={RIGHT_X + 3 * PW - 78} y={midY - 32} width={156} height={64} rx={13} className="bg-onboard-confirm" />
          <text x={RIGHT_X + 3 * PW} y={midY} dominantBaseline="central" className="bg-onboard-label">CONFIRM</text>
        </g>
      )}
      {onDouble && (
        <g className="bg-onboard bg-onboard-dbl" onClick={onDouble} style={{ cursor: 'pointer' }}>
          <rect x={LEFT_X + 3 * PW - 78} y={midY - 32} width={156} height={64} rx={13} className="bg-onboard-double" />
          <text x={LEFT_X + 3 * PW} y={midY - 8} dominantBaseline="central" className="bg-onboard-label bg-onboard-label-dark">DOUBLE</text>
          {doubleValue ? <text x={LEFT_X + 3 * PW} y={midY + 15} dominantBaseline="central" className="bg-onboard-sub">→ {doubleValue}</text> : null}
        </g>
      )}
      {offer && (
        <g>
          <rect x={barCx - 168} y={midY - 96} width={336} height={192} rx={18} className="bg-offer-bg" />
          <text x={barCx} y={midY - 56} dominantBaseline="central" className="bg-offer-title">
            {offer.title || 'DOUBLE'} → {offer.value}
          </text>
          <g className="bg-onboard" onClick={offer.onTake} style={{ cursor: 'pointer' }}>
            <rect x={barCx - 152} y={midY - 22} width={146} height={100} rx={13} className="bg-onboard-confirm" />
            <text x={barCx - 79} y={midY + 30} dominantBaseline="central" className="bg-onboard-label">TAKE</text>
          </g>
          <g className="bg-onboard" onClick={offer.onDrop} style={{ cursor: 'pointer' }}>
            <rect x={barCx + 6} y={midY - 22} width={146} height={100} rx={13} className="bg-offer-drop" />
            <text x={barCx + 79} y={midY + 30} dominantBaseline="central" className="bg-onboard-label bg-offer-drop-label">DROP</text>
          </g>
        </g>
      )}
    </svg>
  );
}
